import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store.js';
import { Button, Input, Card } from '../../components/ui.jsx';
import { api, ApiError } from '../../api.js';

export default function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null); setLoading(true);
    try {
      await login(email, password, needsTotp ? totp : null);
      navigate(params.get('next') || '/dashboard');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'totp_required') {
        setNeedsTotp(true);
      } else {
        setError(err.message || 'Sign-in failed');
      }
    } finally { setLoading(false); }
  };

  const onMagic = async () => {
    if (!email) { setError('Enter your email first'); return; }
    setError(null); setLoading(true);
    try {
      await api.magicLinkRequest({ email, purpose: 'login' });
      setMagicSent(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell>
      <h1 className="text-2xl font-semibold text-ink-900 mb-1">Welcome back</h1>
      <p className="text-sm text-ink-500 mb-6">Sign in to your Skillbase workspace.</p>

      {magicSent ? (
        <Card>
          <h3 className="text-base font-semibold text-ink-900 mb-1">Check your inbox</h3>
          <p className="text-sm text-ink-500">We sent a sign-in link to <strong>{email}</strong>. The link expires in 15 minutes.</p>
        </Card>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <Input label="Email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} />
          <Input label="Password" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} />
          {needsTotp ? (
            <Input label="Two-factor code" type="text" inputMode="numeric" pattern="[0-9]{6}" autoComplete="one-time-code" required value={totp} onChange={e => setTotp(e.target.value)} hint="6-digit code from your authenticator app" />
          ) : null}
          {error ? <p className="text-sm text-danger">{error}</p> : null}
          <Button type="submit" loading={loading} className="w-full">Sign in</Button>
          <button type="button" onClick={onMagic} className="block w-full text-center text-sm text-primary-700 hover:text-primary-900">Email me a sign-in link instead</button>
        </form>
      )}

      <p className="text-sm text-ink-500 mt-6 text-center">
        New to Skillbase? <Link to="/signup" className="text-primary-700 font-medium">Start a workspace</Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ children, wide }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-ink-50 via-white to-primary-50 grid place-items-center px-4 py-12">
      <div className={wide ? 'w-full max-w-2xl' : 'w-full max-w-md'}>
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-700 to-accent-600 grid place-items-center text-white font-bold">S</div>
          <div className="text-xl font-semibold text-ink-900">Skillbase</div>
        </div>
        <Card className="p-8">
          {children}
        </Card>
        <p className="text-xs text-ink-400 text-center mt-6">
          Australian data residency · Built for AU mid-market and enterprise.
        </p>
      </div>
    </div>
  );
}
